import type { ReactNode } from 'react'
import Link from 'next/link'
import { cn, Display } from '@/components/ui'

/**
 * Le vocabulaire de la carte de verre — en-tête et lien.
 *
 * ------------------------------------------------------------------------
 * Pourquoi ici et pas dans `src/components/ui`
 * ------------------------------------------------------------------------
 * Le kit est partagé par les cinq espaces, et `cn` est un simple `clsx` : ses
 * primitives exposent des **variantes fermées** et refusent qu'on les bricole
 * par `className` depuis l'appelant — deux utilitaires de couleur en conflit se
 * départageraient par l'ordre de la feuille, pas par l'intention.
 *
 * `Eyebrow` est `text-ink-mute`, sans variante de ton. Lui en ajouter une
 * toucherait un composant qui n'est pas dans le périmètre de ce lot. Ces deux
 * éléments ne servent qu'aux trois écrans d'entrée : ils vivent donc à côté
 * d'eux, en clair, sans rien surcharger.
 *
 * ------------------------------------------------------------------------
 * ⚠️ La lavande, et où elle a le droit de se poser
 * ------------------------------------------------------------------------
 * `text-lavender-deep` est proscrit sur fond sombre — 2,57:1, la règle est
 * écrite dans `app.css`. La maquette l'emploie pourtant sur la carte de verre :
 * c'est son défaut, il n'est pas recopié.
 *
 * Mais `lavender` non plus ne suffit pas partout. Mesuré sur la carte au
 * plancher du voile (voir `layout.tsx`) : **4,11:1**. C'est au-dessus du seuil
 * de 3:1 — celui du texte large et des éléments non textuels — et en dessous du
 * seuil de 4,5:1 du texte courant. D'où le partage, qui est la seule décision
 * de couleur de ce fichier :
 *
 *   · **Titre** — Playfair 28px, gras. « Grand texte » au sens WCAG
 *     (≥ 18,66px gras), seuil 3:1. Le mot accentué est en `lavender`. ✔
 *   · **Lien** — 13px. Seuil 4,5:1. Le **texte** passe en crème plein
 *     (10,26:1), et l'accent lavande est porté par le **filet** sous le mot, qui
 *     est un élément non textuel : seuil 3:1, donc 4,11:1 suffit. ✔
 *
 * La lavande reste ainsi l'accent de l'écran, sans jamais porter seule un mot
 * qu'il faut lire. Le survol échange le filet en crème plutôt que de jouer sur
 * l'opacité, comme le veut la charte.
 */

/**
 * En-tête de la carte : surtitre, titre Playfair au mot accentué, chapeau.
 *
 * ⚠️ **Le titre et son mot accentué sont deux propriétés, pas une chaîne à
 * découper** — même raison que dans `BandeauHero` : « Se *connecter* » accentue
 * le second mot, « On vous renvoie un *accès* » le quatrième, et « Vous vous
 * formez à quel *titre ?* » emporte la ponctuation. Aucune règle de découpe ne
 * tient les trois.
 */
export function EnTeteVerre({
  surtitre,
  titre,
  accent,
  children,
}: {
  surtitre: string
  /** Le titre, sans le mot accentué. */
  titre: string
  /** Le mot mis en italique lavande, à la fin du titre. */
  accent: string
  /** Le chapeau, s'il y en a un. */
  children?: ReactNode
}) {
  return (
    <div className="flex flex-col gap-2.5">
      {/* Crème 75 % et non les 60 % de la maquette. Les 60 % passeraient
          (4,80:1) : ce n'est donc pas le contraste qui tranche, mais la règle du
          lot — plancher de 75 % d'opacité sur du texte — et la cohérence avec
          les libellés de `Field` en ton sombre, qui sont eux aussi à 75 %. Deux
          gris de texte sur la même carte, c'était un de trop. */}
      <p className="text-[11px] font-bold tracking-[0.09em] text-cream/75 uppercase">{surtitre}</p>

      {/* `font-bold` explicite : `.u-app h1` impose 800 et l'emporterait sur les
          700 de `.u-display`, les deux étant dans la couche `base`. La maquette
          dessine ce titre en 700 ; un utilitaire tranche sans discussion. */}
      <Display
        as="h1"
        className="text-[clamp(28px,3.2vw,36px)] leading-[1.04] font-bold tracking-[-0.03em] text-cream"
      >
        {titre} <em className="italic text-lavender">{accent}</em>
      </Display>

      {children && <p className="text-[13.5px] leading-[1.55] text-cream/75">{children}</p>}
    </div>
  )
}

/**
 * Lien sur la carte de verre. Voir l'avertissement sur la lavande.
 *
 * ⚠️ **Le filet est un `text-decoration`, pas une bordure basse** — et c'est le
 * seul point où ce composant s'écarte de la convention du dépôt, qui souligne
 * les liens par la `border-bottom` posée sur `a` dans `tokens.css`.
 *
 * La raison est mesurable à l'œil : deux de ces liens portent une cible tactile
 * de 44px, obtenue par de la marge intérieure. Une bordure se dessine au bas de
 * la **boîte**, donc à 12px sous le texte — un trait qui flotte, détaché du mot
 * qu'il souligne. Le `text-decoration` suit la ligne de base et reste collé au
 * texte quelle que soit la hauteur de la boîte.
 *
 * `border-b-0` neutralise du même coup la bordure héritée de `a:hover`, sans
 * quoi le survol dessinerait deux traits parallèles.
 */
export function LienVerre({
  href,
  className,
  children,
}: {
  href: string
  className?: string
  children: ReactNode
}) {
  return (
    <Link
      href={href}
      className={cn(
        'border-b-0 font-semibold text-cream underline decoration-lavender decoration-1 underline-offset-[4px] hover:decoration-cream',
        className,
      )}
    >
      {children}
    </Link>
  )
}
